import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { ArrowRight } from "lucide-react";
import { useMemo, useState } from "react";
import { EventRegistrationDialog } from "@/components/events/EventRegistrationDialog";
import { useEventRegistrationState } from "@/components/events/useEventRegistrationState";
import { EmphasizedTitle } from "@/components/site/EmphasizedTitle";
import { formatSiteDateRange } from "@/lib/datetime.schedule";
import { listPublishedEvents } from "@/lib/events.functions";
import { englishOnlyContentLang } from "@/lib/locale";
import { pageHead } from "@/lib/seo";
import { cn } from "@/lib/utils";
import { Skeleton } from "@/components/ui/skeleton";
import { m } from "@/paraglide/messages.js";
import { getLocale } from "@/paraglide/runtime";

export const Route = createFileRoute("/events")({
  head: () =>
    pageHead({
      title: m.events_meta_title(),
      description: m.events_meta_description(),
      path: "/events",
    }),
  component: EventsPage,
});

function EventsPage() {
  const eventsQ = useQuery({
    queryKey: ["site-events"],
    queryFn: () => listPublishedEvents(),
  });
  const [registerEvent, setRegisterEvent] = useState<
    Awaited<ReturnType<typeof listPublishedEvents>>[number] | null
  >(null);

  const events = useMemo(() => {
    const list = eventsQ.data ?? [];
    return [...list].sort(
      (a, b) => a.start_at.localeCompare(b.start_at) || a.created_at.localeCompare(b.created_at),
    );
  }, [eventsQ.data]);

  const { countMap, registeredIds } = useEventRegistrationState(events);
  const locale = getLocale();
  // Event titles, audience and details are English-only DB text (no per-locale columns yet).
  const contentLang = englishOnlyContentLang(locale);

  return (
    <>
      <section className="bg-ink">
        <div className="container-page page-hero-pad">
          <span className="eyebrow">{m.events_eyebrow()}</span>
          <h1 className="mt-3 font-display text-hero leading-[0.95] md:text-8xl">
            <EmphasizedTitle lead={m.events_title_lead()} accent={m.events_title_accent()} />
          </h1>
          <p className="mt-5 max-w-xl text-foreground/80">{m.events_intro()}</p>
        </div>
      </section>

      <section className="container-page page-follow-pad">
        {eventsQ.isLoading && (
          <div className="grid min-h-[28rem] border-t border-l border-white/10 md:grid-cols-2">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="space-y-4 border-r border-b border-white/10 bg-ink p-9">
                <Skeleton className="h-3 w-28" />
                <Skeleton className="h-8 w-3/4" />
                <Skeleton className="h-4 w-1/2" />
                <Skeleton className="h-16 w-full" />
                <Skeleton className="h-10 w-32" />
              </div>
            ))}
          </div>
        )}

        {!eventsQ.isLoading && events.length === 0 && (
          <p className="text-foreground/65">{m.events_empty()}</p>
        )}

        {events.length > 0 && (
          <div className="grid border-t border-l border-white/10 md:grid-cols-2">
            {events.map((event) => {
              const isOver = new Date(event.end_at).getTime() < Date.now();
              const counts = countMap.get(event.id);
              const isRegistered = registeredIds.has(event.id);
              const isFull = counts ? counts.spotsRemaining <= 0 : false;

              return (
                <article
                  key={event.id}
                  className={cn(
                    "border-r border-b border-white/10 bg-ink p-9",
                    isOver && "opacity-70",
                  )}
                >
                  <div className="flex flex-wrap items-center gap-3">
                    <div
                      className={cn(
                        "font-display text-xs tracking-[0.18em]",
                        isOver ? "text-foreground/75" : "text-ember",
                      )}
                    >
                      {formatSiteDateRange(event.start_at, event.end_at, locale)}
                    </div>
                    {isOver && (
                      <span className="border border-white/15 px-2 py-0.5 font-display text-xs tracking-[0.18em] text-foreground/75">
                        {m.events_over()}
                      </span>
                    )}
                    {!isOver && isRegistered && (
                      <span className="border border-ember/30 bg-ember/10 px-2 py-0.5 font-display text-xs tracking-[0.18em] text-ember">
                        {m.events_registered()}
                      </span>
                    )}
                    {!isOver && !isRegistered && isFull && (
                      <span className="border border-white/15 px-2 py-0.5 font-display text-xs tracking-[0.18em] text-foreground/75">
                        {m.events_full()}
                      </span>
                    )}
                  </div>
                  <h2 lang={contentLang} className="mt-2 font-display text-2xl">
                    {event.title}
                  </h2>
                  <div lang={contentLang} className="mt-1 text-sm text-foreground/75">
                    {event.audience}
                  </div>
                  <p lang={contentLang} className="mt-4 text-sm text-foreground/80">
                    {event.details}
                  </p>
                  {!isOver && counts && (
                    <p className="mt-3 text-xs text-foreground/75">
                      {m.events_spots_left({ n: counts.spotsRemaining })}
                    </p>
                  )}
                  {!isOver && !isRegistered && !isFull && (
                    <button
                      type="button"
                      onClick={() => setRegisterEvent(event)}
                      className="mt-6 inline-flex items-center gap-2 border border-white/30 px-4 py-2.5 font-display text-xs tracking-[0.18em] hover:bg-white/5"
                    >
                      {m.events_register()} <ArrowRight className="h-4 w-4 rtl:rotate-180" />
                    </button>
                  )}
                </article>
              );
            })}
          </div>
        )}

        <div className="mt-14 border border-white/10 bg-black/40 p-10 text-center">
          <h3 className="font-display text-3xl">{m.events_group_cta_title()}</h3>
          <p className="mt-3 text-sm text-foreground/70">{m.events_group_cta_body()}</p>
          <Link to="/contact" className="btn-ember btn-ember-hover mt-6 inline-flex">
            {m.events_get_in_touch()} <ArrowRight className="h-5 w-5 rtl:rotate-180" />
          </Link>
        </div>
      </section>

      <EventRegistrationDialog
        event={registerEvent}
        open={!!registerEvent}
        onClose={() => setRegisterEvent(null)}
      />
    </>
  );
}
